// External Libraries
import React from "react";
import * as Icon from "react-feather";
import moment from "moment";

import queryString from "query-string";
import Board from "react-trello";
import { Trello, ListChecks } from "lucide-react";
import clsx from "clsx";

// Services
import TaskService from "../../services/TaskService";
import UserService from "../../services/UserService";
import UtilityService from "../../services/UtilityService";

// Common Components
import WComponent from "../common/WComponent";
import TaskModal from "./task-modal/TaskModal";
import TaskFilterModal from "./task-filter-modal/TaskFilterModal";
import Tabs from "../common/tabs/Tabs";
import Tab from "../common/tabs/tab/Tab";
import ViewList from "../workflow/ViewList/ViewList";
import UserBubbles from "../common/UserBubbles/UserBubbles";
import DeleteConfirmationModal from "../common/modal/confirmation_modal/ConfirmationModal";
import SearchInput from "../common/search_input/SearchInput";
import Button from "../common/button/Button";
import PageHeading from "../common/page_heading/PageHeading";
import Loader from "../common/loader/Loader";
import ScrollableContainer from "../common/scrollable_container/ScrollableContainer";

// Constants
import WORKFLOW from "../../constants/Workflow";
import { DEADLINES, ORDER_QUERIES, STATUSES } from "../../constants/Tasks";

// Styling
import "./tasks.css";
import Checkbox from "../common/checkbox/Checkbox";
import PermissionGate from "../../permissions/permissionGate";
import RestrictedAccess from "../common/restricted_access/RestrictedAccess";

const KANBAN_STYLES = {
	deadline: "deadline",
	status: "status"
};

const { alert } = UtilityService;

class Tasks extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			loading: true,

			tasks: [],
			taskId: "new",
			search: "",

			sortField: ORDER_QUERIES.deadline,
			sortOrder: "asc",

			showTaskModal: false,
			showKanban: true,
			showFilterModal: false,
			showViews: true,
			showFilters: true,
			showConfirmDelete: false,
			showDeleteModal: false,

			pageView: "list",
			kanbanStyle: KANBAN_STYLES.status,

			isViewChanged: false,
			isAllSelected: false,

			clientId: 0,
			engagementUserId: 0,
			assignedStatuses: [],
			dueDate: WORKFLOW.defaultDateFunction,
			engagementId: 0,
			multiEditCount: 0,

			assignedUsers: [],
			assignedClients: [],
			assignedClientGroups: [],
			selectedTaskIds: [],
			showDeleteConfirmation: false,
			onConfirmDeleteTaskProxyState: null,
			isNotAllowedResource: false,
		};

		this.taskModal = null;
		this.onNewTask = this.onNewTask.bind(this);
		this.onTaskSelect = this.onTaskSelect.bind(this);
		this.onTaskClose = this.onTaskClose.bind(this);
		this.onKanbanConfirmDeleteClick = this.onKanbanConfirmDeleteClick.bind(this);
	}

	componentDidMount() {
		this.resetComponent();
	}

	componentDidUpdate(prevProps) {
		let newProps = this.props;
		let oldProps = prevProps;

		if (
			oldProps.engagementId !== newProps.engagementId ||
			oldProps.title !== newProps.title ||
			oldProps.showViews !== newProps.showViews ||
			oldProps.showFilters !== newProps.showFilters
		) {
			this.resetComponent();
		}
	}

	resetComponent = async () => {
		let { clientId, engagementId, title, showViews, showFilters } = this.props;

		let user = await UserService.getUserData();

		let isViewingTasksOnMyTasksPage = UtilityService.isNullish(engagementId) && UtilityService.isNullish(clientId);

		let assignedUsers = [];

		if (isViewingTasksOnMyTasksPage) {
			assignedUsers = [
				{
					user_id: user.userId,
					userName: user.firstName + " " + user.lastName
				}
			];
		}

		await this.update({
			title: title || "Tasks",
			clientId: clientId || 0,
			engagementId: engagementId || 0,

			// The default view should be the currently logged in users tasks
			assignedUsers: assignedUsers,
			assignedClientGroups: [],
			assignedClients: [],
			assignedStatuses: [],
			dueDate: WORKFLOW.defaultDateFunction,
			showDeleteConfirmation: false,
			onConfirmDeleteTaskProxyState: null,

			showViews,
			showFilters
		});

		await this.update({
			loading: true
		});

		await this.fetchTasks();

		await this.update({
			loading: false
		});

		if (this.props.location.search) {
			let params = queryString.parse(this.props.location.search);

			if (params.taskId) {
				this.onTaskSelect(params.taskId);
			}
		}
	};

	onSelectAll = e => {
		let value = e.target.checked;
		let { tasks } = this.state;

		let newTasks = tasks.slice();
		let multiEditCount = value ? newTasks.length : 0; // Set count based on selection state

		for (let task of newTasks) {
			task._isSelected = value;
		}

		this.update({
			isAllSelected: value,
			tasks: newTasks, // Fix key name from newTasks to tasks
			multiEditCount
		});
	};

	onTaskSelected = taskId => {
		let { tasks, multiEditCount } = this.state;
		let newtasks = tasks.slice();
		let countChange = 0;

		for (let task of newtasks) {
			if (task.id === taskId) {
				task._isSelected = !task._isSelected;
				countChange = task._isSelected ? 1 : -1;
			}
		}

		this.setState({
			tasks: newtasks,
			multiEditCount: multiEditCount + countChange
		});
	};

	sortBy = async field => {
		let { sortOrder } = this.state;

		await this.update({
			sortField: field,
			sortOrder: sortOrder === "desc" ? "asc" : "desc"
		});

		await this.fetchTasks();
	};

	fetchTasks = async () => {
		let {
			search,
			sortField,
			sortOrder,
			clientId,
			engagementId,
			assignedUsers,
			assignedStatuses,
			assignedClients,
			assignedClientGroups,
			dueDate
		} = this.state;

		let tasks = await TaskService.list({
			searchTerm: search,
			sortOrder,
			sortField,

			clientId,
			engagementId,

			assignedUsers,
			assignedStatuses,
			assignedClients,
			assignedClientGroups,
			dueDate
		});

		if(UtilityService.isNotAllowedResource({response : tasks}))
		{
			await this.update({
				isNotAllowedResource : true
			});	
			return;		
		}

		await this.update({
			tasks
		});
	};

	onSearch = async event => {
		await this.update({
			search: event.target.value
		});

		await this.fetchTasks();
	};

	onNewTask = async () => {
		await this.update({
			taskId: "new",
			showTaskModal: true
		});
	};

	onTaskSelect = async taskId => {
		this.update({
			taskId: taskId,
			showTaskModal: true
		});
	};

	onTaskClose = async () => {
		await this.update({
			loading: true,
			showTaskModal: false
		});

		await this.fetchTasks();

		await this.update({
			loading: false
		});
	};

	onShowKanban = () => {
		this.update({
			showKanban: true
		});
	};

	onHideKanban = () => {
		this.update({
			showKanban: false
		});
	};

	truncateTaskName = ({ name }) => {
		return name.length > 20 ? name.substring(0, 20) + " ..." : name;
	};

	rerenderTask = async ({ task }) => {
		let { tasks } = this.state;

		let newTasks = tasks.slice();

		for (let t of tasks) {
			if (t.id === task.id) {
				t.name = task.name;
				t.description = task.description;
				t.status = task.status;
			}
		}

		await this.update({
			tasks: newTasks
		});
	};

	onTaskStatusUpdate = async ({ taskId, status }) => {
		let updatedTask = null;

		try {
			updatedTask = await TaskService.update({ taskId, status });

			alert({ type: "success", text: "Updated Successfully!" });
		} catch (error) {
			console.log(error);
			alert({ type: "error", text: "Updated error ..." });
		}

		await this.rerenderTask({ task: updatedTask });
	};

	onCardMoveAcrossLanes = (fromLaneId, toLaneId, cardId, index) => {
		this.onTaskStatusUpdate({ taskId: cardId, status: toLaneId });
	};

	onCardClick = (cardId, laneId) => {
		this.onTaskSelect(cardId);
	};

	onDefaultViewSelected = async () => {
		await this.resetComponent();
	};

	onViewSelected = async ({ view }) => {
		let { filters, sort } = JSON.parse(view.schema);

		let { clientId, engagementId, assignedUsers, assignedStatuses, assignedClients, assignedClientGroups, dueDate } = filters;
		let { sortField, sortOrder } = sort;

		await this.update({
			engagementId,
			clientId,

			assignedUsers,
			assignedStatuses,
			assignedClients,
			assignedClientGroups,
			dueDate,

			sortField,
			sortOrder,
			isViewChanged: false
		});

		await this.fetchTasks({});
	};

	getFilterSchema = () => {
		// TODO - Should add search to the view
		let { sortField, sortOrder, clientId, engagementId, assignedUsers, assignedStatuses, assignedClients, assignedClientGroups, dueDate } = this.state;

		let schema = {
			filters: {
				clientId,
				engagementId,

				assignedUsers,
				assignedStatuses,
				assignedClients,
				assignedClientGroups,
				dueDate
			},
			columns: [], // Add functionality later
			sort: {
				sortField,
				sortOrder
			}
		};

		return schema;
	};

	buildStatusKanbanStyle = () => {
		let { tasks } = this.state;

		let lanes = Object.values(STATUSES).map(status => {
			return {
				id: status.id,
				title: status.name,
				cards: [],
				style: {
					backgroundColor: status.color
				},
				cardStyle: {
					borderLeft: `4px solid ${status.color}`
				}
			};
		});

		for (let task of tasks) {
			for (let lane of lanes) {
				if (task.status === lane.id) {
					lane.cards.push({
						id: task.id,
						task
					});
				}
			}
		}

		const data = {
			lanes
		};

		return data;
	};

	buildDeadlineKanbanStyle = () => {
		let { tasks } = this.state;

		let lanes = Object.values(DEADLINES).map(deadline => {
			return {
				id: deadline.id,
				title: deadline.name,
				range: deadline.range,
				cards: [],
				style: {
					backgroundColor: deadline.color
				},
				cardStyle: {
					borderLeft: `4px solid ${deadline.color}`
				}
			};
		});

		// Object keeps track if a task has been categorized already
		let categorizedTasks = {};

		for (let task of tasks) {
			for (let lane of lanes) {
				// Parse deadline from the task payload
				let deadline = moment(task.deadline);

				// Range is stored within the lange object
				let { start, end } = lane.range;

				// Confirm if the task is within the deadline range
				let isWithinDeadlineRange = deadline.isBetween(start, end, "hour", "[]");

				// Confirm that the task is within the range set for this particular lane and that we have not already categorized it before
				if (isWithinDeadlineRange && !categorizedTasks[task.id]) {
					// Cache a true value to indicate we have categorized this particular task
					categorizedTasks[task.id] = true;

					// Push the task on top the react-trello payload
					lane.cards.push({
						id: task.id,
						task
						// title: this.truncateTaskName({ name: task.name }),
						// description: task.description,
						// label: moment(task.deadline).format("YYYY-MM-DD"),
						// metadata: task
					});
				}
			}
		}

		const data = {
			lanes
		};

		return data;
	};

	async deleteTask(taskId, engagementId) {
		try {
			await TaskService.update({ taskId, engagementId, isDeleted: true });
			alert({ type: "success", text: "Updated Successfully!" });
			this.resetComponent();
		} catch (error) {
			alert({ type: "error", text: "Task update Error!" });
			console.error(error);
		}
	}

	async onKanbanDeleteClick(taskId, engagementId) {
		await this.update({
			showDeleteConfirmation: true,
			onConfirmDeleteTaskProxyState: {
				taskId,
				engagementId
			}
		});
	}

	async onKanbanConfirmDeleteClick({ confirmed, proxyState }) {
		await this.update({ showDeleteConfirmation: false });
		if (!confirmed) {
			return;
		}

		await this.deleteTask(proxyState.taskId, proxyState.engagementId);
		await this.update({ onConfirmDeleteTaskProxyState: null });
	}

	CustomCard = props => {
		let { id, task, onClick } = props;

		return (
			<div key={id} className="task-card" onClick={onClick}>
				{task.Client && <div className="task-card__label">Client: {task.Client ? task.Client.name : ""}</div>}

				<div className="task-card__divider" />

				<div className="task-card__title">{task.name}</div>
				<div className="task-card__description">{task.description}</div>

				<div className="task-card__divider" />

				{task.Engagement && <div className="task-card__eng-name">{task.Engagement.name}</div>}

				<div className="task-card__dates">
					<div className="task-card__dates__item">Created - {moment(task.created_at).format("YYYY-MM-DD")}</div>
					<div className="task-card__dates__item">Due - {moment(task.deadline).format("YYYY-MM-DD")}</div>
				</div>
			</div>
		);
	};

	renderKanban() {
		let { kanbanStyle, engagementId } = this.state;

		let data = {};

		if (kanbanStyle === KANBAN_STYLES.status) {
			data = this.buildStatusKanbanStyle();
		} else if (kanbanStyle === KANBAN_STYLES.deadline) {
			data = this.buildDeadlineKanbanStyle();
		}

		return (
			<>
				<div className="task-kanban-container">
					<Board
						components={{
							Card: this.CustomCard
						}}
						cardDraggable={kanbanStyle === KANBAN_STYLES.status}
						className="task-kanban-container__board"
						style={{ backgroundColor: "#eaedf3", overflowX: "scroll" }}
						data={data}
						onCardClick={this.onCardClick}
						onCardMoveAcrossLanes={this.onCardMoveAcrossLanes}
						onCardDelete={taskId => this.onKanbanDeleteClick(taskId, engagementId)}
					/>
				</div>

				<DeleteConfirmationModal
					title="Delete Task?"
					warning="Are you sure you want to delete this task?"
					show={this.state.showDeleteConfirmation}
					onClose={this.onKanbanConfirmDeleteClick}
					proxyState={this.state.onConfirmDeleteTaskProxyState}
				/>
			</>
		);
	}

	renderSortIcon = field => {
		let { sortField, sortOrder } = this.state;

		return sortField === field && (sortOrder === "desc" ? <Icon.ChevronUp size={14} /> : <Icon.ChevronDown size={14} />);
	};

	onPageViewSelect = async pageView => {
		await this.update({ pageView: pageView.id });
	};

	onKanbanStyleSelect = async kanbanStyle => {
		await this.update({ kanbanStyle: kanbanStyle.id });
	};

	onTaskFilterModalClose = async () => {
		await this.update({ showFilterModal: false });
	};

	onShowFilterModal = async () => {
		await this.update({
			showFilterModal: true
		});
	};

	onFilterChange = async ({ filters, columns }) => {
		let { clientId, engagementId, assignedUsers, assignedStatuses, assignedClients, assignedClientGroups, dueDate } = filters;

		await this.update({
			clientId,
			engagementId,

			assignedUsers,
			assignedStatuses,
			assignedClients,
			assignedClientGroups,
			dueDate,

			isViewChanged: true
		});

		await this.fetchTasks({});
	};

	openDeleteModal = async () => {
		let { tasks } = this.state;

		let selectedTasks = tasks.filter(task => task._isSelected);
		let selectedTaskIds = selectedTasks.map(task => task.id);

		await this.update({
			selectedTaskIds,
			showDeleteModal: true
		});
	};

	onMultiUpdate = async () => {
		await this.update({
			loading: true
		});

		// data: selectedTaskIds
		let { selectedTaskIds } = this.state;

		// note: hardcoded delete for now
		await TaskService.bulkUpdate({ taskIds: selectedTaskIds, action: "delete" });

		// fetch tasks
		await this.fetchTasks();

		// reset related state
		await this.update({
			loading: false,
			selectedTaskIds: [],
			multiEditCount: 0
		});
	};

	closeDeleteModal = async ({ confirmed }) => {
		if (confirmed) {
			await this.onMultiUpdate();
		}

		// close modal
		await this.update({
			showDeleteModal: false
		});
	};

	render() {
		let {
			loading,
			taskId,

			search,

			// Filters
			clientId,
			engagementId,
			assignedUsers,
			assignedStatuses,
			assignedClients,
			assignedClientGroups,
			dueDate,
			kanbanStyle,
			pageView,

			showTaskModal,
			showFilterModal,
			showViews,
			showFilters,
			showConfirmDelete,

			title,

			tasks,
			isAllSelected,

			isViewChanged,
			multiEditCount,
			showDeleteModal,
			isNotAllowedResource
		} = this.state;

		if (isNotAllowedResource)
		{					
			return <RestrictedAccess/>
		}

		return (
			<div className="tasks">
				<PageHeading section="Workflow" title={title} />

				<div className="tasks__header">
					{multiEditCount > 0 && <Button view="secondary" onClick={this.openDeleteModal} text={`Delete Selected ${multiEditCount}`} />}
					<PermissionGate resource="task" action="toggleViews">
						<Tabs onSelect={this.onPageViewSelect} selected={pageView}>
							<Tab id={"list"} icon={<ListChecks size={20} strokeWidth={1.5} />} />
							<Tab id={"kanban"} icon={<Trello size={20} strokeWidth={1.5} />} />
						</Tabs>
					</PermissionGate>
					<PermissionGate resource="task" action="deadlineStatusView">
						<Tabs onSelect={this.onKanbanStyleSelect} selected={kanbanStyle}>
							<Tab id={"deadline"} text="Deadline" />
							<Tab id={"status"} text="Status" />
						</Tabs>
					</PermissionGate>
					<PermissionGate resource="task" action="showFilters">
					{showFilters && (
						<Button
							className={clsx("tasks__header__icon-btn", "ft-tasks-filter-btn")}
							view="secondary"
							onClick={this.onShowFilterModal}
							icon={<Icon.Sliders size={18} />}
						/>
					)}
					</PermissionGate>

					<PermissionGate resource="task" action="create">
						<Button 
						className={"ft-task-add"}
						view="secondary"
						onClick={this.onNewTask}
						icon={<Icon.Plus size={18} />} 
						/>
					</PermissionGate>

					<PermissionGate resource="task" action="search">
						<SearchInput onChange={this.onSearch} value={search} />
					</PermissionGate>
				</div>

				<div className="test-container">
				<PermissionGate resource="task" action="showViewsList">
						{showViews && (
							<ViewList
								type={"task"}
								isViewChanged={isViewChanged}
								getSchema={this.getFilterSchema}
								onViewSelected={this.onViewSelected}
								onDefaultViewSelected={this.onDefaultViewSelected}
							/>
						)}
					</PermissionGate>

					{loading && (
						<div className="container-loader">
							<Loader />
						</div>
					)}

					{tasks.length === 0 && !loading && (
						<div className="container-empty">
							<div>
								<Icon.Frown size={64} />
							</div>
							<div>... no tasks ...</div>
						</div>
					)}

					{tasks.length > 0 && !loading && (
						<ScrollableContainer>
							{pageView == "kanban" && this.renderKanban()}
							{pageView == "list" && (
								<div className={clsx("t", "t--compact", "t--rotate-180")}>
									<div className="t__header">
										<div onClick={() => {}} className={clsx("t__header__item", "clients__header__item--checkbox")}>
											<Checkbox name="selectAll" checked={isAllSelected} onChange={this.onSelectAll} isSmall />
										</div>

										<div
											onClick={() => this.sortBy(ORDER_QUERIES.organization)}
											className={clsx("t__header__item", "tasks__header__item--client-name")}
										>
											Organization {this.renderSortIcon(ORDER_QUERIES.organization)}
										</div>

										<div onClick={() => this.sortBy(ORDER_QUERIES.name)} className={clsx("t__header__item", "ft-task-title")}>
											Title {this.renderSortIcon(ORDER_QUERIES.name)}
										</div>
										<div onClick={() => this.sortBy(ORDER_QUERIES.description)} className="t__header__item">
											Description {this.renderSortIcon(ORDER_QUERIES.description)}
										</div>
										<div className="t__header__item">Users</div>
										<div onClick={() => this.sortBy(ORDER_QUERIES.status)} className={clsx("t__header__item", "ft-task-status")}>
											Status {this.renderSortIcon(ORDER_QUERIES.status)}
										</div>
										<div onClick={() => this.sortBy(ORDER_QUERIES.deadline)} className={clsx("t__header__item", "ft-task-deadline")}>
											Deadline {this.renderSortIcon(ORDER_QUERIES.deadline)}
										</div>
									</div>

									{tasks.map((task, index) => {
										let odd = index % 2 === 0;

										return (
											<div
												className={clsx("t__row", { "t__row--stripe": odd })}
												key={index}
												style={{ backgroundColor: STATUSES[task.status].color }}
												onClick={() => this.onTaskSelect(task.id)}
												data-taskid={task.id}
											>
												<div className={clsx("t__row__item", "clients__row__item--checkbox")}>
													<Checkbox
														name="selectAll"
														checked={task._isSelected}
														onChange={event => this.onTaskSelected(task.id)}
														isSmall
													/>
												</div>

												<div className={clsx("t__row__item", "tasks__row-item--client-name")}>{task.Client ? task.Client.name: ""}</div>
												<div className={clsx("t__row__item", "ft-task-name-col")}>{task.name}</div>
												<div className={clsx("t__row__item", "ft-task-desc-col")}>{task.description}</div>
												<div className={clsx("t__row__item")}>
													<div id="user-column" className={clsx("t__row__item", "ft-task-user-col")}>
														{<UserBubbles engagementUsers={task.task_users} />}
													</div>
												</div>
												<div className={clsx("t__row__item", "ft-task-status-col")}>{WORKFLOW.taskStatus[task.status].value}</div>
												<div className={clsx("t__row__item", "ft-task-deadline-col")}>
													{moment(task.deadline).format("MMMM Do YYYY @ h:mma")}
												</div>
											</div>
										);
									})}
								</div>
							)}
						</ScrollableContainer>
					)}
				</div>

				<TaskModal show={showTaskModal} clientId={clientId} taskId={taskId} engagementId={engagementId} onClose={this.onTaskClose} {...this.props} />
				<TaskFilterModal
					show={showFilterModal}
					onClose={this.onTaskFilterModalClose}
					onFilterChange={this.onFilterChange}
					filters={{ clientId, engagementId, assignedUsers, assignedStatuses, assignedClients, assignedClientGroups, dueDate }}
				/>

				<DeleteConfirmationModal
					title={`Delete ${multiEditCount} Tasks?`}
					warning="Are you sure you want to delete this tasks?"
					show={showDeleteModal}
					onClose={({ confirmed }) => {
						this.closeDeleteModal({ confirmed });
					}}
				/>
			</div>
		);
	}
}

export default Tasks;
